import { contentNodesToMarkdown, markdownToContentDoc, markdownToContentNodes, newFigures } from '@zuruflex/core';
import type { ContentKind, ContentNode } from '@zuruflex/types';
import {
  Alert,
  Button,
  ContentDocument,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@zuruflex/ui';
import type { Editor } from '@tiptap/react';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { ZuruflexApiError } from '@zuruflex/api-client';

const ACTIONS = ['draft', 'rewrite', 'shorten', 'expand', 'simplify', 'fix', 'continue', 'translate'] as const;
type Action = (typeof ACTIONS)[number];
const LANGUAGES = ['English', 'French', 'Kinyarwanda'] as const;

/** Actions that work on text already on the page rather than on a request. */
const NEEDS_SELECTION: readonly Action[] = ['rewrite', 'shorten', 'expand', 'simplify', 'fix', 'translate'];

/** How much of the page before the caret `continue` is shown. */
const CONTEXT_CHARACTERS = 4000;

export interface AiWriterProps {
  editor: Editor;
  pageTitle: string;
  pageKind: ContentKind;
}

/**
 * "Write with AI": a row under the toolbar that asks the writing model for
 * a suggestion and shows it for review.
 *
 * Nothing the model says reaches the page until somebody presses Insert,
 * and what is inserted lands in a *draft* — an owner still publishes it.
 * The suggestion is converted by `markdownToContentNodes`, which can only
 * build the editor's own node types, so it cannot smuggle in a link, an
 * image or markup whatever the model replies.
 */
export function AiWriter({ editor, pageTitle, pageKind }: AiWriterProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [action, setAction] = useState<Action>('rewrite');
  const [instruction, setInstruction] = useState('');
  const [language, setLanguage] = useState<(typeof LANGUAGES)[number]>('French');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<{
    markdown: string;
    from: number;
    to: number;
    /** Everything the model was given, so a number it was not given can be spotted. */
    given: string;
  } | null>(null);

  const generate = async () => {
    const { from, to, empty } = editor.state.selection;
    setError(null);

    let text = '';
    if (action === 'continue') {
      const before = editor.state.doc.slice(0, to).content.toJSON() as ContentNode[];
      text = contentNodesToMarkdown(before).slice(-CONTEXT_CHARACTERS);
    } else if (NEEDS_SELECTION.includes(action)) {
      if (empty) return setError(t('admin:content.ai.selectFirst'));
      text = contentNodesToMarkdown(editor.state.selection.content().content.toJSON() as ContentNode[]);
    } else if (!instruction.trim()) {
      return setError(t('admin:content.ai.describeFirst'));
    }

    setPending(true);
    try {
      const result = await api.writeContent({
        action,
        text,
        instruction: instruction.trim() || undefined,
        targetLanguage: action === 'translate' ? language : undefined,
        pageTitle: pageTitle || 'Untitled',
        pageKind,
      });
      setSuggestion({ markdown: result.markdown, from, to, given: `${text}\n${instruction}` });
    } catch (cause) {
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected'));
    } finally {
      setPending(false);
    }
  };

  // Figures the model was not given. It is told never to invent one; this is the check.
  const added = suggestion ? newFigures(suggestion.given, suggestion.markdown) : [];

  const insert = () => {
    if (!suggestion) return;
    const size = editor.state.doc.content.size;
    const from = Math.min(suggestion.from, size);
    const to = Math.min(suggestion.to, size);
    const nodes = markdownToContentNodes(suggestion.markdown);
    // Continuing adds after the caret; everything else replaces the selection.
    const range = action === 'continue' ? { from: to, to } : { from, to };
    editor.chain().focus().insertContentAt(range, nodes).run();
    setSuggestion(null);
  };

  return (
    <div className="min-w-0 space-y-3 border-t border-border-default px-3 py-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {ACTIONS.map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={action === value ? 'default' : 'outline'}
            className="h-7 px-2.5 text-xs"
            aria-pressed={action === value}
            onClick={() => {
              setAction(value);
              setError(null);
            }}
          >
            {t(`admin:content.ai.action.${value}`)}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {action === 'translate' ? (
          <Select value={language} onValueChange={(value) => setLanguage(value as (typeof LANGUAGES)[number])}>
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void generate();
              }
            }}
            maxLength={1000}
            aria-label={t('admin:content.ai.instruction')}
            placeholder={t(`admin:content.ai.placeholder.${action}`)}
            className="h-8 min-w-[16rem] flex-1"
          />
        )}
        <Button type="button" size="sm" isLoading={pending} onClick={() => void generate()}>
          <Sparkles />
          {t('admin:content.ai.generate')}
        </Button>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {suggestion ? (
        <div className="space-y-3 rounded-xl border border-border-default bg-surface p-3">
          <p className="text-xs font-medium text-text-muted">{t('admin:content.ai.review')}</p>
          <div className="max-h-72 min-w-0 overflow-y-auto break-words">
            <ContentDocument doc={markdownToContentDoc(suggestion.markdown)} size="sm" />
          </div>
          {added.length > 0 ? (
            <Alert variant="warning">
              {t('admin:content.ai.figures', { figures: added.join(', ') })}
            </Alert>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setSuggestion(null)}>
              {t('admin:content.ai.discard')}
            </Button>
            <Button type="button" variant="outline" size="sm" isLoading={pending} onClick={() => void generate()}>
              {t('admin:content.ai.again')}
            </Button>
            <Button type="button" size="sm" onClick={insert}>
              {action === 'continue' ? t('admin:content.ai.insertAfter') : t('admin:content.ai.insert')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
